import React, { useMemo, useState } from 'react'
import {
  Badge,
  DataTable,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { ClaimHistory as ClaimHistoryType } from '@dataTypes/insurance.types'
import { filteredClaimsData } from '@utils/claims.util'
import { formatDate } from '@utils/date.utils'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import {
  getClaimStatusColor,
  useInsuranceBadgeMode,
} from '@components/pages/Insurance/insuranceBadge'

interface ClaimHistoryProps {
  historyData: ClaimHistoryType[]
  outlined?: boolean
  onSubmitClaim?: () => void
}

type ClaimRecord = Record<string, unknown>
type ClaimRow = { id: string }
type ClaimSortColumn = 'id' | 'date' | 'claimAmount' | 'amountPaid' | 'status'

const DEFAULT_PAGE_SIZE = 10

const isFullClaimRow = (row: unknown): boolean =>
  Boolean(
    row &&
      typeof row === 'object' &&
      Array.isArray((row as { claimitems?: unknown }).claimitems),
  )

const formatClaimHistoryDate = (row: ClaimRecord): string => {
  const raw = row.createdAt ?? row.claim_date
  if (typeof raw !== 'string' || raw.length === 0) return '—'
  const formatted = formatDate(raw)
  return formatted.includes('NaN') ? raw : formatted
}

const getClaimHistoryRows = (
  historyData: ClaimHistoryType[],
): ClaimRecord[] => {
  if (!Array.isArray(historyData) || historyData.length === 0) return []

  if (historyData.every(isFullClaimRow)) {
    try {
      return (
        filteredClaimsData({ claims: historyData })?.filter(
          (claim: { generalStatus?: string }) =>
            claim.generalStatus === 'Approved' ||
            claim.generalStatus === 'Partially Approved',
        ) ?? []
      )
    } catch {
      return []
    }
  }

  return historyData as unknown as ClaimRecord[]
}

const getClaimId = (row: ClaimRecord): string =>
  (row.providerClaimId as string) ??
  (row.public_id as string) ??
  (row.claim_id as string) ??
  (row.id as string) ??
  '—'

const getClaimAmount = (row: ClaimRecord): string =>
  (row.claimAmount as string) ?? (row.claim_amount as string) ?? '—'

const getApprovedAmount = (row: ClaimRecord): string =>
  (row.amountPaid as string) ?? '—'

const getClaimStatus = (row: ClaimRecord): string | undefined =>
  (row.generalStatus ?? row.status) as string | undefined

const parseAmount = (value: string): number => {
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

const getSortValue = (
  row: ClaimRecord,
  column: ClaimSortColumn,
): string | number => {
  switch (column) {
    case 'id':
      return getClaimId(row).toLowerCase()
    case 'date': {
      const raw = row.createdAt ?? row.claim_date
      const time = typeof raw === 'string' ? new Date(raw).getTime() : 0
      return Number.isNaN(time) ? 0 : time
    }
    case 'claimAmount':
      return parseAmount(getClaimAmount(row))
    case 'amountPaid':
      return parseAmount(getApprovedAmount(row))
    case 'status':
      return (getClaimStatus(row) ?? '').toLowerCase()
  }
}

const ClaimHistory: React.FC<ClaimHistoryProps> = ({
  historyData,
  outlined = false,
  onSubmitClaim,
}) => {
  const badgeMode = useInsuranceBadgeMode()
  const claims = useMemo(() => getClaimHistoryRows(historyData), [historyData])
  const [sortColumnId, setSortColumnId] = useState<ClaimSortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const sortedClaims = useMemo(() => {
    if (!sortColumnId) return claims
    const direction = sortDirection === 'asc' ? 1 : -1
    return [...claims].sort((a, b) => {
      const left = getSortValue(a, sortColumnId)
      const right = getSortValue(b, sortColumnId)
      if (left < right) return -1 * direction
      if (left > right) return 1 * direction
      return 0
    })
  }, [claims, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedClaims.length / pageSize))
  const currentPage = Math.min(page, totalPages)

  const { rows, claimsById } = useMemo(() => {
    const byId = new Map<string, ClaimRecord>()
    const pageClaims = sortedClaims.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    )
    const pageRows: ClaimRow[] = pageClaims.map((claim, index) => {
      const id = `${getClaimId(claim)}-${(currentPage - 1) * pageSize + index}`
      byId.set(id, claim)
      return { id }
    })
    return { rows: pageRows, claimsById: byId }
  }, [sortedClaims, currentPage, pageSize])

  const columns = useMemo<DataTableColumn<ClaimRow>[]>(
    () => [
      {
        id: 'id',
        label: 'Claim ID',
        sortable: true,
        renderCell: ({ id }) => {
          const claim = claimsById.get(id)
          return claim ? getClaimId(claim) : null
        },
      },
      {
        id: 'date',
        label: 'Date',
        sortable: true,
        renderCell: ({ id }) => {
          const claim = claimsById.get(id)
          return claim ? formatClaimHistoryDate(claim) : null
        },
      },
      {
        id: 'claimAmount',
        label: 'Claim amount',
        sortable: true,
        renderCell: ({ id }) => {
          const claim = claimsById.get(id)
          return claim ? getClaimAmount(claim) : null
        },
      },
      {
        id: 'amountPaid',
        label: 'Approved ins.',
        sortable: true,
        renderCell: ({ id }) => {
          const claim = claimsById.get(id)
          return claim ? getApprovedAmount(claim) : null
        },
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        renderCell: ({ id }) => {
          const claim = claimsById.get(id)
          const status = claim ? getClaimStatus(claim) : undefined
          if (!status) return '—'
          return (
            <Badge
              label={status}
              color={getClaimStatusColor(status)}
              mode={badgeMode}
            />
          )
        },
      },
    ],
    [claimsById, badgeMode],
  )

  const handleSort = (columnId: string): void => {
    const column = columnId as ClaimSortColumn
    if (column === sortColumnId) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumnId(column)
      setSortDirection('asc')
    }
    setPage(1)
  }

  if (claims.length === 0) {
    return (
      <div className="section-claim-history">
        <p className="section-title">Claims history</p>
        <div className="section-claim-history--empty">
          <InsuranceEmptyCard
            iconClass="mgc mgc_receive_money_fill"
            emptyTitle="No claims found"
            description="You don't have any claim history on record yet."
            actionText="Submit claim"
            onAction={onSubmitClaim}
            outlined={outlined}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="section-claim-history">
      <p className="section-title">Claims history</p>
      <div className="insurance-table">
        <DataTable<ClaimRow>
          columns={columns}
          rows={rows}
          showActions={false}
          sortColumnId={sortColumnId ?? undefined}
          sortDirection={sortColumnId ? sortDirection : undefined}
          onSort={handleSort}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size)
              setPage(1)
            },
          }}
        />
      </div>
    </div>
  )
}

export default ClaimHistory
